import React, { FunctionComponent, useCallback } from 'react';
import { FaTrash } from 'react-icons/fa6';
import { useDispatch } from 'react-redux';
import { authSelectors } from '../../../../app/auth/auth.selectors';
import { IChart } from '../../../../app/resource/chart/chart.types';
import { deleteChart, selectChartDraft, selectChartVersion } from '../../../../app/page/chart/chart.actions';
import { openDialog } from '../../../../app/dialog/dialog.actions';
import { userSelectors } from '../../../../app/resource/user/user.selectors';
import { IUser } from '../../../../app/resource/user/user.types';
import { useApiRequestState } from '../../../../utils/api.utils';
import { convertAndFormatDateToUserTimeZone } from '../../../../utils/date.utils';
import { useSelect, useSelectWithParams } from '../../../../utils/selector.utils';
import { ChartSidebarItem } from './ChartSidebarItem';

function getVersionLabel(version: IChart, userTimezone: IUser['timezone']): string {
  if (version.lastPublishedAt && userTimezone) {
    return convertAndFormatDateToUserTimeZone(version.lastPublishedAt, userTimezone, 'MM/DD/YYYY h:mm A');
  }
  return `Version ${version.id}`;
}

export interface IChartVersionSidebarProps {
  versions: IChart[];
  selectedChartId: IChart['id'] | null;
  draftChart: IChart | null;
  hasDraft: boolean;
  isDraftSelected: boolean;
}

export const ChartVersionSidebar: FunctionComponent<IChartVersionSidebarProps> = ({
  versions,
  selectedChartId,
  draftChart,
  hasDraft,
  isDraftSelected,
}) => {
  const dispatch = useDispatch();
  const [isDeletingChart] = useApiRequestState('deleteChart');
  const loggedUserId = useSelect(authSelectors.selectLoggedUser);
  const loggedUser = useSelectWithParams(userSelectors.selectResourceById, loggedUserId ?? '');
  const userTimezone = loggedUser?.timezone ?? null;

  const onSelectDraft = useCallback(() => {
    dispatch(selectChartDraft());
  }, [dispatch]);

  const onSelectVersion = useCallback(
    (chartId: IChart['id']) => {
      dispatch(selectChartVersion(chartId));
    },
    [dispatch],
  );

  const onDeleteVersion = useCallback(
    (version: IChart) => {
      if (isDeletingChart) {
        return;
      }

      const versionLabel = getVersionLabel(version, userTimezone);
      dispatch(
        openDialog('confirmation', {
          title: 'Delete version',
          message: `Delete version ${versionLabel}? This cannot be undone.`,
          confirmButton: 'Delete',
          onConfirm: () => dispatch(deleteChart(version.id)),
        }),
      );
    },
    [dispatch, isDeletingChart, userTimezone],
  );

  // Match chart column height and allow the version list below to scroll independently.
  const sidebarRootStyle: React.CSSProperties = {
    borderRight: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch',
    minHeight: 0,
  };

  if (!hasDraft && versions.length === 0) {
    return (
      <div className="col max-250 settings-inner-left-menu p-2" style={sidebarRootStyle}>
        <p className="darkGray mb-0">No versions yet.</p>
      </div>
    );
  }

  return (
    <div className="col max-250 settings-inner-left-menu p-2" style={sidebarRootStyle}>
      {hasDraft && draftChart && (
        <ChartSidebarItem
          title={<span className="bold">Draft</span>}
          chart={draftChart}
          isSelected={isDraftSelected}
          onClick={onSelectDraft}
          buttonTitle="Edit draft chart"
          showDateInSubtitle
        />
      )}
      <h3 className="bold mb-2 mt-8 f-1">Versions</h3>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {versions.length === 0 ? (
          <p className="darkGray mb-0">No versions yet.</p>
        ) : (
          versions.map((version) => {
            const isSelected = !isDraftSelected && String(version.id) === String(selectedChartId);
            const versionLabel = getVersionLabel(version, userTimezone);

            return (
              <div key={version.id} className="flex aic mb-1">
                <ChartSidebarItem
                  title={
                    <>
                      {versionLabel}
                      {version.published ? <span className="ml-1 f-0">(Published)</span> : null}
                    </>
                  }
                  chart={version}
                  isSelected={isSelected}
                  onClick={() => onSelectVersion(version.id)}
                  buttonTitle={`View version ${versionLabel}`}
                  className="btn darkGray mb-0 mx-0 px-1"
                  style={{ flex: 1 }}
                />
                {!version.published && (
                  <button
                    type="button"
                    className="remove-button"
                    title={`Delete version ${versionLabel}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onDeleteVersion(version);
                    }}
                    disabled={Boolean(isDeletingChart)}
                    style={{
                      minWidth: 40,
                      marginLeft: 5,
                      ...(isDeletingChart ? { opacity: 0.6, pointerEvents: 'none' } : {}),
                    }}
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
